import React, { useState } from 'react';
import {
  Plus,
  Pencil,
  Trash2,
  CheckCircle2,
  XCircle,
  ClipboardList,
  LayoutDashboard,
  Tag,
  ToggleLeft,
  ToggleRight,
} from 'lucide-react';
import { FieldResearch } from '../../types/fieldResearch';
import { getResearches, saveResearches } from '../../utils/storage';

const inputCls =
  'w-full bg-[#0f0c29] border border-white/10 text-white placeholder-white/25 rounded-xl px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50 transition';

const emptyForm = { name: '', category: '', description: '', status: 'active' as 'active' | 'inactive' };

export default function AdminPesquisasPage() {
  const [researches, setResearches] = useState<FieldResearch[]>(getResearches);
  const [modal, setModal] = useState<{ open: boolean; editing: FieldResearch | null }>({
    open: false,
    editing: null,
  });
  const [form, setForm] = useState(emptyForm);

  const persist = (data: FieldResearch[]) => {
    setResearches(data);
    saveResearches(data);
  };

  const openNew = () => {
    setForm(emptyForm);
    setModal({ open: true, editing: null });
  };

  const openEdit = (r: FieldResearch) => {
    setForm({ name: r.name, category: r.category, description: r.description, status: r.status });
    setModal({ open: true, editing: r });
  };

  const handleDelete = (id: string) => {
    if (!window.confirm('Excluir esta pesquisa?')) return;
    persist(researches.filter((r) => r.id !== id));
  };

  const handleToggleStatus = (id: string) => {
    persist(
      researches.map((r) =>
        r.id === id ? { ...r, status: r.status === 'active' ? 'inactive' : 'active' } : r,
      ),
    );
  };

  const handleSave = () => {
    if (!form.name.trim()) return;
    if (modal.editing) {
      persist(researches.map((r) => (r.id === modal.editing!.id ? { ...r, ...form } : r)));
    } else {
      const newR: FieldResearch = {
        ...form,
        id: `pesquisa-${Date.now()}`,
        createdAt: new Date().toISOString(),
      };
      persist([...researches, newR]);
    }
    setModal({ open: false, editing: null });
  };

  const activeCount = researches.filter((r) => r.status === 'active').length;
  const inactiveCount = researches.filter((r) => r.status === 'inactive').length;

  return (
    <div className="min-h-full bg-[#07051a] text-white">
      <div className="max-w-4xl mx-auto px-6 py-8 space-y-6">
        {/* Page header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg font-bold text-white">Pesquisas de Campo</h1>
            <p className="text-xs text-white/30 mt-0.5">Gerencie os tipos de pesquisa disponíveis para coleta</p>
          </div>
          <button
            onClick={openNew}
            className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-purple-600 to-violet-500 hover:from-purple-500 hover:to-violet-400 text-white text-sm font-semibold rounded-xl transition-all shadow-lg shadow-purple-600/25"
          >
            <Plus size={14} strokeWidth={2.5} />
            Nova Pesquisa
          </button>
        </div>
        {/* Stats */}
        <div className="grid grid-cols-3 gap-4">
          {[
            { label: 'Total', value: researches.length, icon: LayoutDashboard, color: 'from-purple-600/20 to-violet-600/10 border-purple-500/20', text: 'text-purple-300' },
            { label: 'Ativas', value: activeCount, icon: CheckCircle2, color: 'from-emerald-600/20 to-green-600/10 border-emerald-500/20', text: 'text-emerald-300' },
            { label: 'Inativas', value: inactiveCount, icon: XCircle, color: 'from-zinc-600/20 to-zinc-600/10 border-zinc-500/20', text: 'text-zinc-400' },
          ].map(({ label, value, icon: Icon, color, text }) => (
            <div key={label} className={`bg-gradient-to-br ${color} border rounded-2xl px-5 py-4`}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-white/40 font-medium">{label}</span>
                <Icon size={14} className={text} />
              </div>
              <p className={`text-3xl font-bold ${text}`}>{value}</p>
            </div>
          ))}
        </div>

        {/* Section title */}
        <div className="flex items-center gap-3">
          <h2 className="text-xs font-semibold text-white/30 uppercase tracking-widest">
            Todas as pesquisas
          </h2>
          <div className="flex-1 h-px bg-white/5" />
        </div>

        {/* List */}
        <div className="space-y-3">
          {researches.length === 0 ? (
            <div className="bg-white/3 border border-dashed border-white/10 rounded-2xl py-20 text-center">
              <ClipboardList size={36} className="mx-auto mb-4 text-white/15" />
              <p className="text-white/30 text-sm font-medium">Nenhuma pesquisa cadastrada</p>
              <p className="text-white/15 text-xs mt-1">Clique em "Nova Pesquisa" para começar</p>
              <button
                onClick={openNew}
                className="mt-5 inline-flex items-center gap-2 px-4 py-2 bg-purple-600/20 border border-purple-500/30 text-purple-300 text-xs font-semibold rounded-xl hover:bg-purple-600/30 transition"
              >
                <Plus size={12} />
                Criar primeira pesquisa
              </button>
            </div>
          ) : (
            researches.map((r) => (
              <div
                key={r.id}
                className="group bg-white/[0.04] hover:bg-white/[0.07] border border-white/8 hover:border-white/15 rounded-2xl px-5 py-4 flex items-center gap-4 transition-all duration-200"
              >
                {/* Status indicator */}
                <div
                  className={`shrink-0 w-2 h-2 rounded-full ${
                    r.status === 'active' ? 'bg-emerald-400 shadow-[0_0_8px_#34d39966]' : 'bg-white/15'
                  }`}
                />

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-semibold text-white">{r.name}</p>
                    {r.category && (
                      <span className="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-purple-500/15 border border-purple-500/20 text-purple-300">
                        <Tag size={9} />
                        {r.category}
                      </span>
                    )}
                    <span
                      className={`text-[10px] px-2 py-0.5 rounded-full border font-medium ${
                        r.status === 'active'
                          ? 'bg-emerald-500/10 border-emerald-500/25 text-emerald-300'
                          : 'bg-white/5 border-white/10 text-white/25'
                      }`}
                    >
                      {r.status === 'active' ? 'Ativa' : 'Inativa'}
                    </span>
                  </div>
                  {r.description && (
                    <p className="text-xs text-white/30 mt-1 truncate max-w-sm">{r.description}</p>
                  )}
                  <p className="text-[10px] text-white/15 mt-1.5">
                    Criada em {new Date(r.createdAt).toLocaleDateString('pt-BR')}
                  </p>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    title={r.status === 'active' ? 'Desativar' : 'Ativar'}
                    onClick={() => handleToggleStatus(r.id)}
                    className="p-2 rounded-lg hover:bg-white/10 transition text-white/30 hover:text-white"
                  >
                    {r.status === 'active' ? <ToggleRight size={16} className="text-emerald-400" /> : <ToggleLeft size={16} />}
                  </button>
                  <button
                    title="Editar"
                    onClick={() => openEdit(r)}
                    className="p-2 rounded-lg hover:bg-blue-600/15 transition text-white/30 hover:text-blue-300"
                  >
                    <Pencil size={14} />
                  </button>
                  <button
                    title="Excluir"
                    onClick={() => handleDelete(r.id)}
                    className="p-2 rounded-lg hover:bg-red-600/15 transition text-white/30 hover:text-red-300"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Modal */}
      {modal.open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          onClick={(e) => e.target === e.currentTarget && setModal({ open: false, editing: null })}
        >
          <div className="bg-[#110e2b] border border-white/10 rounded-2xl w-full max-w-md shadow-2xl shadow-black/60 overflow-hidden">
            {/* Modal header */}
            <div className="px-6 py-5 border-b border-white/8 flex items-center gap-3">
              <div className="w-8 h-8 rounded-xl bg-purple-600/20 border border-purple-500/30 flex items-center justify-center">
                <ClipboardList size={14} className="text-purple-300" />
              </div>
              <div>
                <h2 className="text-sm font-bold text-white">
                  {modal.editing ? 'Editar Pesquisa' : 'Nova Pesquisa'}
                </h2>
                <p className="text-[11px] text-white/30">
                  {modal.editing ? 'Atualize os dados da pesquisa' : 'Preencha os dados da nova pesquisa'}
                </p>
              </div>
            </div>

            <div className="px-6 py-5 space-y-4">
              <div>
                <label className="block text-[11px] font-semibold text-white/40 uppercase tracking-wider mb-2">
                  Nome *
                </label>
                <input
                  className={inputCls}
                  placeholder="Ex: Pesquisa de Salões de Beleza"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-white/40 uppercase tracking-wider mb-2">
                  Categoria
                </label>
                <input
                  className={inputCls}
                  placeholder="Ex: Beleza, Saúde, Alimentação"
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-white/40 uppercase tracking-wider mb-2">
                  Descrição
                </label>
                <textarea
                  className={`${inputCls} resize-none`}
                  rows={3}
                  placeholder="Objetivo desta pesquisa..."
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-white/40 uppercase tracking-wider mb-2">
                  Status
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {(['active', 'inactive'] as const).map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setForm({ ...form, status: s })}
                      className={`py-3 rounded-xl border text-xs font-semibold transition-all flex items-center justify-center gap-2 ${
                        form.status === s
                          ? s === 'active'
                            ? 'bg-emerald-600/20 border-emerald-500/40 text-emerald-300'
                            : 'bg-zinc-600/20 border-zinc-500/40 text-zinc-300'
                          : 'bg-white/3 border-white/8 text-white/30 hover:bg-white/8'
                      }`}
                    >
                      {s === 'active' ? (
                        <><CheckCircle2 size={13} /> Ativa</>
                      ) : (
                        <><XCircle size={13} /> Inativa</>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Modal footer */}
            <div className="px-6 pb-6 flex gap-3">
              <button
                onClick={() => setModal({ open: false, editing: null })}
                className="flex-1 py-3 rounded-xl bg-white/5 border border-white/10 text-white/40 text-sm font-medium hover:bg-white/10 hover:text-white/60 transition"
              >
                Cancelar
              </button>
              <button
                onClick={handleSave}
                disabled={!form.name.trim()}
                className="flex-1 py-3 rounded-xl bg-gradient-to-r from-purple-600 to-violet-500 hover:from-purple-500 hover:to-violet-400 disabled:opacity-30 text-white text-sm font-semibold transition shadow-lg shadow-purple-600/20"
              >
                {modal.editing ? 'Salvar alterações' : 'Criar pesquisa'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
